/**
 * User-flow step 1 — invited team members fill in their personal profile:
 * phone (required), photo URL (optional), and Bar Council number (optional
 * but recommended for advocate-tier roles).
 */
import { useState } from "react";
import { toast } from "sonner";
import { UserCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PhoneInput } from "@/components/PhoneInput";

export default function ProfileStep({ state, onAdvance }) {
    const [form, setForm] = useState({
        phone: state.details?.phone || "",
        photo_url: state.details?.photo_url || "",
        bar_council_number: state.details?.bar_council_number || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.phone || form.phone.trim().length < 7) {
            toast.error("Phone number is required"); return;
        }
        setSaving(true);
        try {
            await api.patch("/onboarding/profile", form);
            toast.success("Profile saved");
            onAdvance?.();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="max-w-md mx-auto space-y-5">
            <div className="text-center mb-2">
                <UserCircle2 className="w-12 h-12 text-primary mx-auto" strokeWidth={1.25}/>
                <h2 className="text-2xl font-semibold tracking-tight text-foreground mt-3">Your profile</h2>
                <p className="text-muted-foreground text-sm mt-2">A few quick details so your team can reach you.</p>
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Mobile number *</div>
                <PhoneInput value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} testid="onboarding-profile-phone"/>
            </div>

            <Field label="Photo URL" hint="Paste a link to your headshot — we'll wire upload in a later release."
                testId="onboarding-profile-photo"
                value={form.photo_url} onChange={v => setForm(f => ({ ...f, photo_url: v }))}
                placeholder="https://…"/>

            <Field label="Bar Council enrolment number"
                hint="Optional — required to file matters via eCourts."
                testId="onboarding-profile-bcn"
                value={form.bar_council_number} onChange={v => setForm(f => ({ ...f, bar_council_number: v }))}
                placeholder="MH/2018/12345"/>

            <button type="submit" disabled={saving} data-testid="onboarding-profile-save"
                className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-medium">
                {saving ? "Saving…" : "Continue"}
            </button>
        </form>
    );
}

function Field({ label, hint, value, onChange, testId, ...rest }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            <input value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
                {...rest}
                className="w-full bg-background border border-border focus:border-primary outline-none px-4 py-2.5 text-sm"/>
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}
